// The single composition-root audio mount (client-audio spec R8-unmount/R8-screen-exit;
// design DD1/DD3/DD10; tasks.md 7.3). `initAudio` is installed once in `App.tsx` as a sibling
// to `SessionController` (plan D4): it constructs the guarded `localStorage` preference adapter
// (R7), the two-axis autoplay lifecycle (R6), and the one store-subscription driver (R2/R3/R4),
// wires the `NullAudioEngine → WebAudioEngine` arming swap into the driver, subscribes the
// driver to the store, and returns an `AudioController` whose `dispose()` tears the whole layer
// down (unsubscribe + engine `dispose()`) on unmount / before an HMR replace. Audio outlives any
// one screen or match, so a standalone mount is testable in isolation and never entangles
// `SessionController`'s lifecycle.
//
// Route observation (DD10, review-2 F2 / P2-F2 / P5-F1): current-screen identity is owned by
// react-router, NOT the match store, so a store-only driver cannot see "left Colony/Match for
// Lobby/Summary while `matchResult` is still null". The mount therefore carries a thin
// `useLocation` route observer (`AudioRouteObserver`) that reports the active route to the
// controller; the controller fires `stopAll()` when the route LEAVES an in-match screen and
// re-requests the current season/tier bed when it RE-ENTERS one (symmetric to the exit) — both
// independent of any `matchResult` edge. The first route observed is a baseline (no signal), so
// the initial mount neither stops nor restarts, and a crossing between the two in-match screens
// (the elimination demotion /colony → /match) is inert (both are in-match).

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useClientStore } from '../state/store';
import type { AudioContextLike, AudioEngine } from './engine';
import { NullAudioEngine } from './engine';
import { createAudioDriver } from './audio-driver';
import type { AudioDriver } from './audio-driver';
import { createAudioLifecycle } from './lifecycle';
import type { AudioLifecycle } from './lifecycle';
import { AUDIO_PREFS_KEY, createAudioPrefsStore } from './audio-prefs-store';
import type { AudioPrefsStore, StorageLike } from './audio-prefs-store';
import type { SoundControlSeams } from '../ui/components/hud/SoundControl';

/** The routes on which audio plays (the in-match screens, UI.md §3). A route leaving this set
 * for Lobby (`/`) or Summary (`/summary`) is a screen exit; a route re-entering it is a
 * re-entry. Kept in one place so the observer and the ambient-bed re-sync agree (DD10). */
const IN_MATCH_ROUTES: ReadonlySet<string> = new Set(['/colony', '/match']);

/** The minimal store surface the mount consumes: an imperative read (baseline seed) plus the
 * `subscribe(prev→state)` seam the driver's edge detection runs on. `useClientStore` satisfies
 * it; a test passes the same real store, seeded via fixtures. */
export type AudioStore = Pick<typeof useClientStore, 'getState' | 'subscribe'>;

export interface InitAudioDeps {
  /** The client store to observe. Defaults to the singleton `useClientStore`. */
  store?: AudioStore;
  /** The durable preference adapter (R7). Defaults to a guarded `window.localStorage` adapter. */
  prefs?: AudioPrefsStore;
  /** Constructs the `AudioContext` on the first unlock. Defaults to a real `AudioContext`
   * (browser only; never invoked in jsdom/node, where a test injects a stub). */
  createContext?: () => AudioContextLike;
  /** Builds the engine over the context. Defaults (in the lifecycle) to a real `WebAudioEngine`;
   * a test injects the recording fake so the driver's live engine is assertable. */
  createEngine?: (ctx: AudioContextLike) => AudioEngine;
}

/** The composition-root audio handle. `sound` threads into the in-match `SoundControl`;
 * `notifyRoute` is fed by the `useLocation` observer; `dispose()` is the unmount/HMR teardown. */
export interface AudioController {
  /** The seam bundle the in-match `SoundControl` renders from (the shared lifecycle + prefs). */
  readonly sound: SoundControlSeams;
  /** Report the active route path; the controller derives in-match enter/exit and drives the
   * driver's `routeEntered()` / `routeExited()`. The first call is a baseline (no signal). */
  notifyRoute(pathname: string): void;
  /** Tear the layer down: unsubscribe from the store, dispose the driver, and dispose the
   * lifecycle (which disposes the engine + detaches the `statechange` listener). Idempotent. */
  dispose(): void;
}

/** A guarded `window.localStorage` shim (accessed lazily inside each call, so even a throwing
 * property access degrades — the adapter wraps every call in try/catch anyway, R7). Mirrors
 * `main.tsx`'s `windowSessionStore` shape for the new `localStorage` scope. */
function defaultLocalStorage(): StorageLike {
  return {
    getItem: (key) => window.localStorage.getItem(key),
    setItem: (key, value) => window.localStorage.setItem(key, value),
    removeItem: (key) => window.localStorage.removeItem(key),
  };
}

/** The production `AudioContext` factory (browser only). Feature-detects the standard and the
 * legacy `webkitAudioContext` constructor (older iOS/WebView, spec R1 "no-AudioContext
 * environments"), and THROWS a clear error when neither exists rather than dereferencing an
 * undefined global — the lifecycle's `ensureContext` catches that throw and stays on the
 * `NullAudioEngine`, locked, instead of crashing the first tap (implementation-review F3). Cast at
 * this single boundary: the real Web Audio API satisfies the narrow structural `AudioContextLike`
 * port at runtime, but its overloaded `connect()`/typed events do not statically unify with it. */
function defaultCreateContext(): AudioContextLike {
  const w = globalThis as unknown as {
    AudioContext?: new () => unknown;
    webkitAudioContext?: new () => unknown;
  };
  const Ctor = w.AudioContext ?? w.webkitAudioContext;
  if (Ctor === undefined) {
    throw new Error('Web Audio API unavailable: no AudioContext constructor');
  }
  return new Ctor() as unknown as AudioContextLike;
}

/**
 * Install the audio layer at the composition root (design DD1). Constructs the prefs adapter,
 * the lifecycle, and the driver; wires the arming swap (`onEngineArmed → driver.armEngine`);
 * seeds the driver baseline from the current store (R3 first-snapshot-only) and subscribes it;
 * and returns the controller the `App.tsx` mount holds. The driver starts on `NullAudioEngine`
 * (every call a no-op, R1) until the first unlock swaps in the real `WebAudioEngine`.
 */
export function initAudio(deps: InitAudioDeps = {}): AudioController {
  const store: AudioStore = deps.store ?? useClientStore;
  const prefs: AudioPrefsStore = deps.prefs ?? createAudioPrefsStore(defaultLocalStorage(), AUDIO_PREFS_KEY);

  const driver: AudioDriver = createAudioDriver({ engine: new NullAudioEngine() });

  const lifecycle: AudioLifecycle = createAudioLifecycle({
    prefs,
    createContext: deps.createContext ?? defaultCreateContext,
    createEngine: deps.createEngine,
    // The `NullAudioEngine → WebAudioEngine` swap the driver keys its state-synchronized bed
    // activation on (DD3): on the first unlock the driver adopts the real engine and requests
    // the current season/tier bed once.
    onEngineArmed: (engine) => driver.armEngine(engine),
  });

  // Seed the edge-detection baseline from the current snapshot (fires nothing, R3), THEN
  // subscribe — so the first genuine store change is diffed against present state, never
  // replayed as a catch-up burst.
  driver.onSnapshot(store.getState());
  const unsubscribe = store.subscribe((state) => driver.onSnapshot(state));

  // Route-observer state: `undefined` until the first route is observed (a baseline, no signal).
  let lastInMatch: boolean | undefined;
  let disposed = false;

  return {
    sound: { lifecycle, prefs },

    notifyRoute(pathname: string): void {
      if (disposed) {
        return;
      }
      const inMatch = IN_MATCH_ROUTES.has(pathname);
      if (lastInMatch === undefined) {
        lastInMatch = inMatch; // first route observed — baseline only, no enter/exit
        return;
      }
      if (inMatch === lastInMatch) {
        return; // no in-match boundary crossed (incl. a /colony ↔ /match crossing)
      }
      lastInMatch = inMatch;
      if (inMatch) {
        driver.routeEntered(); // re-entered an in-match screen — re-sync the current bed (R4)
      } else {
        driver.routeExited(); // left an in-match screen — stop the bed (R8), matchResult-independent
      }
    },

    dispose(): void {
      if (disposed) {
        return;
      }
      disposed = true;
      unsubscribe();
      driver.dispose();
      lifecycle.dispose(); // disposes the engine + detaches the statechange listener (R8)
    },
  };
}

/**
 * The app-root audio mount, as a hook (client-audio DD1/DD10).
 *
 * One `initAudio` controller for the app's lifetime, created in an effect and disposed in its
 * cleanup — so a React unmount / HMR replace tears the engine down (spec R8-unmount) and a
 * StrictMode double-invoke creates a fresh, un-leaked controller. Extracted from `App` verbatim so
 * the rounds-mode root (`app/rounds/RoundsApp.tsx`, implementation-review **F1**) installs the
 * *same* layer rather than a second, subtly different copy of it; `App`'s behaviour is unchanged.
 */
export function useAppAudioController(): AudioController | null {
  const [audio, setAudio] = useState<AudioController | null>(null);
  useEffect(() => {
    const controller = initAudio();
    setAudio(controller);
    return () => {
      setAudio(null);
      controller.dispose();
    };
  }, []);
  return audio;
}

/**
 * The composition-root route observer (design DD10). A thin `useLocation` sibling that reports
 * every active route to the controller so screen-exit teardown and in-match re-entry bed re-sync
 * fire from router-owned screen identity, not from store state. Renders nothing.
 */
export function AudioRouteObserver({ controller }: { controller: AudioController }): null {
  const location = useLocation();
  useEffect(() => {
    controller.notifyRoute(location.pathname);
  }, [controller, location.pathname]);
  return null;
}
